'use client';

import { ProfileHeader } from './ProfileHeader';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useDiscardChangesConfirm } from '@/hooks/use-discard-changes-confirm';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  MapPin,
  Building2,
  Globe,
  Mail,
  Phone,
  Users,
  Gem,
  FileText,
  Tag,
  Plus,
  X,
  Pencil,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PublicBusiness } from '@/types/profile';
import axiosInstance from '@/lib/axios';
import { ProfileLink } from './ProfileLink';
import { displayBusinessAddressFields } from '@/lib/format-address';
import { HtmlRenderer } from '@/components/ui/html-renderer';
import { BusinessProfileLinkedCommunities } from './business-profile-linked-communities';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { SocialLinks } from '@/components/social/social-links';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';

const BUSINESS_PROFILE_TABS = ['profile', 'offers', 'communities'] as const;
type BusinessProfileTabId = (typeof BUSINESS_PROFILE_TABS)[number];

interface BusinessProfileViewProps {
  business: PublicBusiness;
  isEditable?: boolean;
  onEdit?: () => void;
}

const profileBodyProse =
  'sm:prose-base dark:sm:prose-invert max-w-none text-foreground/90 [&_p]:leading-[1.75] [&_strong]:text-foreground [&_strong]:font-semibold [&_a]:text-primary';

export function BusinessProfileView({ business, isEditable, onEdit }: BusinessProfileViewProps) {
  const navigate = useNavigate();
  const { communityId: communityRouteSegment } = useParams<{ communityId?: string }>();
  const [tabParam, setTabParam] = useQueryState(
    'tab',
    parseAsStringLiteral(BUSINESS_PROFILE_TABS).withOptions({
      history: 'replace',
      clearOnDefault: true,
    })
  );
  const [editParam, setEditParam] = useQueryState(
    'edit',
    parseAsStringLiteral(['basic'] as const).withOptions({
      history: 'replace',
      clearOnDefault: true,
    })
  );

  const activeTab: BusinessProfileTabId =
    tabParam === 'communities' && !isEditable ? 'profile' : (tabParam ?? 'profile');
  const basicInfoEditing = isEditable && editParam === 'basic';

  const openFullBusinessEdit = () => {
    if (onEdit) {
      onEdit();
      return;
    }
    const editPath = communityRouteSegment
      ? `/${communityRouteSegment}/create/business/${business.id}`
      : `/create/business/${business.id}`;
    navigate(editPath, { state: { initialData: business } });
  };

  const [headerBusiness, setHeaderBusiness] = useState(business);

  useEffect(() => {
    setHeaderBusiness(business);
  }, [business]);

  const handleBasicInfoSaved = (updated: {
    name: string;
    logo?: string;
    category: string;
    subCategory: string;
    selfDefinedCategory: string | null;
  }) => {
    setHeaderBusiness((prev) => ({
      ...prev,
      name: updated.name,
      logo: updated.logo ?? prev.logo,
      category: updated.category,
      subCategory: updated.subCategory,
      selfDefinedCategory: updated.selfDefinedCategory,
    }));
  };

  const handleTabChange = (value: string) => {
    const next = value as BusinessProfileTabId;
    void setTabParam(next === 'profile' ? null : next);
  };

  return (
    <div className="space-y-8 md:space-y-10 pb-12 md:pb-16">
      <ProfileHeader
        type="business"
        business={headerBusiness}
        isEditable={isEditable}
        isEditingBasic={basicInfoEditing}
        onEdit={isEditable ? openFullBusinessEdit : undefined}
        onBasicCancel={() => void setEditParam(null)}
        onBasicSaved={(updated) => {
          handleBasicInfoSaved(updated);
          void setEditParam(null);
        }}
      />

      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <SegmentedTabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <div className="flex justify-center px-1">
            <SegmentedTabsList variant="profile" className="w-full max-w-lg sm:w-auto">
              <SegmentedTabsTrigger variant="profile" value="profile">
                Profile
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="profile" value="offers">
                Offers
              </SegmentedTabsTrigger>
              {isEditable ? (
                <SegmentedTabsTrigger variant="profile" value="communities">
                  Communities
                </SegmentedTabsTrigger>
              ) : null}
            </SegmentedTabsList>
          </div>
          <SegmentedTabsContent value="profile" className="mt-0">
            <BusinessProfileMainGrid business={headerBusiness} />
          </SegmentedTabsContent>
          <SegmentedTabsContent value="offers" className="mt-0">
            <BusinessOffersSection business={headerBusiness} isEditable={isEditable} />
          </SegmentedTabsContent>
          {isEditable ? (
            <SegmentedTabsContent value="communities" className="mt-0">
              <BusinessProfileLinkedCommunities businessId={headerBusiness.id} />
            </SegmentedTabsContent>
          ) : null}
        </SegmentedTabs>
      </div>
    </div>
  );
}

function parseSaleOffers(saleOffers: PublicBusiness['saleOffers']): string[] {
  try {
    const parsed = Array.isArray(saleOffers)
      ? saleOffers
      : typeof saleOffers === 'string' && saleOffers
        ? JSON.parse(saleOffers)
        : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function BusinessOffersSection({
  business,
  isEditable,
}: {
  business: PublicBusiness;
  isEditable?: boolean;
}) {
  const [offers, setOffers] = useState<string[]>(() => parseSaleOffers(business.saleOffers));
  const [isOffersDialogOpen, setIsOffersDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editOffers, setEditOffers] = useState<string[]>([]);

  useEffect(() => {
    setOffers(parseSaleOffers(business.saleOffers));
  }, [business.saleOffers]);

  const handleSaveOffers = async () => {
    setIsSaving(true);
    try {
      const filteredOffers = editOffers.filter((o) => o.trim() !== '');
      const response = await axiosInstance.patch('/api/user/business', {
        id: business.id,
        name: business.name,
        phone: business.phone,
        email: business.email,
        addressJson: business.addressJson,
        saleOffers: filteredOffers,
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to save offers');
      }

      setOffers(filteredOffers);
      setIsOffersDialogOpen(false);
      toast.success('Offers updated successfully');
    } catch {
      toast.error('Failed to update offers');
      throw new Error('Failed to update offers');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddOfferInput = () => {
    setEditOffers([...editOffers, '']);
  };

  const handleOfferChange = (index: number, value: string) => {
    const newOffers = [...editOffers];
    newOffers[index] = value;
    setEditOffers(newOffers);
  };

  const handleRemoveOffer = (index: number) => {
    const newOffers = [...editOffers];
    newOffers.splice(index, 1);
    setEditOffers(newOffers);
  };

  const offersBaseline = useMemo(() => (offers.length > 0 ? offers : ['']), [offers]);
  const offersDirty =
    isOffersDialogOpen && JSON.stringify(editOffers) !== JSON.stringify(offersBaseline);

  const { requestDiscard, dialog: discardDialog } = useDiscardChangesConfirm();
  const { dialog: navGuardDialog } = useUnsavedChangesGuard({
    when: offersDirty,
  });

  return (
    <>
      {discardDialog}
      {navGuardDialog}
      <Card className="profile-card bg-gradient-to-br from-background to-primary/5 border-primary/10">
        <CardContent className="px-5 md:px-7 py-6 md:py-8">
          <section className="space-y-5 md:space-y-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 md:gap-4">
                <span
                  className="flex h-12 w-12 md:h-14 md:w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-sm ring-1 ring-primary/20"
                  aria-hidden
                >
                  <Tag className="h-6 w-6 md:h-7 md:w-7" />
                </span>
                <div className="min-w-0 flex-1 space-y-3 pt-0.5">
                  <h2 className="font-display text-2xl md:text-3xl lg:text-[2rem] font-bold tracking-tight text-foreground leading-[1.15]">
                    Offers
                  </h2>
                  <div
                    className="h-1 w-14 rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                    aria-hidden
                  />
                </div>
              </div>
              {isEditable ? (
                <Dialog
                  open={isOffersDialogOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      setIsOffersDialogOpen(true);
                      return;
                    }
                    requestDiscard(() => setIsOffersDialogOpen(false), {
                      dirty: offersDirty,
                      onSave: handleSaveOffers,
                    });
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-background"
                      onClick={() => setEditOffers(offers.length > 0 ? [...offers] : [''])}
                    >
                      {offers.length > 0 ? (
                        <>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" /> Add
                        </>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>
                        {offers.length > 0 ? 'Edit Sale Offers' : 'Add Sale Offers'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                      {editOffers.map((offer, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            value={offer}
                            onChange={(e) => handleOfferChange(index, e.target.value)}
                            placeholder="e.g. 10% off on all products"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveOffer(index)}
                          >
                            <X className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddOfferInput}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Another Offer
                      </Button>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <Button
                        variant="outline"
                        onClick={() =>
                          requestDiscard(() => setIsOffersDialogOpen(false), {
                            dirty: offersDirty,
                            onSave: handleSaveOffers,
                          })
                        }
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => void handleSaveOffers().catch(() => undefined)}
                        disabled={isSaving}
                      >
                        {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : null}
            </div>
            {offers.length > 0 ? (
              <div className={profileBodyProse}>
                <ul className="list-disc pl-5 marker:text-primary/70 space-y-2">
                  {offers.map((offer, i) => (
                    <li key={i}>{offer}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {isEditable
                  ? 'No offers yet. Click Add to create your first sale offer.'
                  : 'No offers available right now.'}
              </p>
            )}
          </section>
        </CardContent>
      </Card>
    </>
  );
}

function BusinessProfileMainGrid({ business }: { business: PublicBusiness }) {
  const description = business.description ?? '';
  const speciality = business.speciality ?? '';
  const categoryLabel = formatBusinessCategoryLabel(
    business.category,
    business.subCategory,
    business.selfDefinedCategory
  );
  const serviceArea = business.workArea?.trim() || '';
  const contactPhone = business.phone ?? '';
  const contactEmail = business.email ?? '';
  const website = business.website ?? '';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
      <div className="lg:col-span-2 space-y-6 md:space-y-8">
        <Card className="profile-card">
          <CardContent className="px-5 md:px-7 pb-6 md:pb-8 pt-6 md:pt-8 space-y-0">
            <div className="space-y-12 md:space-y-14">
              {description ? (
                <section className="space-y-5 md:space-y-6">
                  <div className="flex items-start gap-3 md:gap-4">
                    <span
                      className="flex h-12 w-12 md:h-14 md:w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-sm ring-1 ring-primary/20"
                      aria-hidden
                    >
                      <FileText className="h-6 w-6 md:h-7 md:w-7" />
                    </span>
                    <div className="min-w-0 flex-1 space-y-3 pt-0.5">
                      <h2 className="font-display text-2xl md:text-3xl lg:text-[2rem] font-bold tracking-tight text-foreground leading-[1.15]">
                        About us
                      </h2>
                      <div
                        className="h-1 w-14 rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                        aria-hidden
                      />
                    </div>
                  </div>
                  <HtmlRenderer html={description} className={profileBodyProse} />
                </section>
              ) : null}
              {speciality ? (
                <section
                  className={cn(
                    'space-y-5 md:space-y-6',
                    description && 'pt-10 md:pt-12 border-t border-border/30'
                  )}
                >
                  <div className="flex items-start gap-3 md:gap-4">
                    <span
                      className="flex h-12 w-12 md:h-14 md:w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-sm ring-1 ring-primary/20"
                      aria-hidden
                    >
                      <Gem className="h-6 w-6 md:h-7 md:w-7" />
                    </span>
                    <div className="min-w-0 flex-1 space-y-3 pt-0.5">
                      <h2 className="font-display text-2xl md:text-3xl lg:text-[2rem] font-bold tracking-tight text-foreground leading-[1.15]">
                        Our Speciality
                      </h2>
                      <div
                        className="h-1 w-14 rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                        aria-hidden
                      />
                    </div>
                  </div>
                  <HtmlRenderer html={speciality} className={profileBodyProse} />
                </section>
              ) : null}
              {categoryLabel ? (
                <section
                  className={cn(
                    'flex items-start gap-3',
                    (description || speciality) && 'pt-8 md:pt-10 border-t border-border/30'
                  )}
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-sm ring-1 ring-primary/20"
                    aria-hidden
                  >
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                    <h2 className="font-display text-lg md:text-xl font-bold tracking-tight text-foreground leading-tight">
                      Category
                    </h2>
                    <div
                      className="h-1 w-10 rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                      aria-hidden
                    />
                    <p className="text-sm md:text-base font-medium text-foreground/90 leading-snug pt-0.5">
                      {categoryLabel}
                    </p>
                  </div>
                </section>
              ) : null}
              {serviceArea ? (
                <section
                  className={cn(
                    'flex items-start gap-3',
                    (description || speciality || categoryLabel) &&
                      'pt-8 md:pt-10 border-t border-border/30'
                  )}
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-sm ring-1 ring-primary/20"
                    aria-hidden
                  >
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                    <h2 className="font-display text-lg md:text-xl font-bold tracking-tight text-foreground leading-tight">
                      Service area
                    </h2>
                    <div
                      className="h-1 w-10 rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary/60"
                      aria-hidden
                    />
                    <p className="text-sm md:text-base font-medium text-foreground/90 leading-snug pt-0.5">
                      {serviceArea}
                    </p>
                  </div>
                </section>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {business.users && business.users.length > 1 ? (
          <Card className="profile-card">
            <CardHeader className="px-5 md:px-7 pt-6 pb-3 md:pt-7">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20 dark:ring-primary/30">
                  <Users className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1 pt-0.5">
                  <CardTitle className="text-lg md:text-xl font-semibold font-display tracking-tight text-foreground leading-tight p-0">
                    People
                  </CardTitle>
                  <CardDescription className="text-sm leading-relaxed p-0">
                    {`${business.users.length} members linked to this business.`}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 md:px-7 pb-6 md:pb-8 pt-0">
              <ul className="list-none p-0 m-0 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {business.users.map((user) => (
                  <li key={user.id} className="min-w-0">
                    <LinkedPersonRow user={user} />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}

        {business.socialLinks && Object.keys(business.socialLinks).length > 0 && (
          <Card className="profile-card bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 ring-1 ring-primary/15">
            <CardContent className="p-5 md:p-7">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <h2 className="text-lg md:text-xl font-semibold font-display tracking-tight text-foreground">
                    Connect on Social Media
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Follow this business on their social platforms.
                  </p>
                </div>
                <SocialLinks socialLinks={business.socialLinks} size="lg" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="space-y-6 lg:sticky lg:top-20 lg:self-start">
        <Card className="profile-card">
          <CardHeader className="px-5 md:px-6 pt-6 pb-2 md:pt-7">
            <CardTitle className="text-lg font-semibold font-display tracking-tight text-foreground flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20 dark:ring-primary/30">
                {business.users?.length === 1 ? (
                  <Users className="h-5 w-5" aria-hidden />
                ) : (
                  <Phone className="h-5 w-5" aria-hidden />
                )}
              </span>
              {business.users?.length === 1 ? 'People & Contact' : 'Contact'}
            </CardTitle>
            <p className="text-xs text-muted-foreground pl-[52px] leading-relaxed">
              {business.users?.length === 1
                ? 'Member and ways to reach out.'
                : 'Reach out directly.'}
            </p>
          </CardHeader>
          <CardContent className="px-4 md:px-5 pb-6 pt-2 space-y-3">
            {business.users?.length === 1 ? (
              <div className="pb-1">
                <LinkedPersonRow user={business.users[0]} />
              </div>
            ) : null}
            <ContactItem
              label="Phone"
              value={contactPhone}
              icon={<Phone className="h-4 w-4" />}
              href={`tel:${contactPhone}`}
            />
            <ContactItem
              label="Email"
              value={contactEmail}
              icon={<Mail className="h-4 w-4" />}
              href={`mailto:${contactEmail}`}
            />
            <ContactItem
              label="Address"
              value={displayBusinessAddressFields(
                { address: business.address, addressJson: business.addressJson },
                false
              )}
              icon={<MapPin className="h-4 w-4" />}
              href={
                typeof business.addressJson === 'object' &&
                business.addressJson !== null &&
                'googleMapLink' in business.addressJson &&
                typeof business.addressJson.googleMapLink === 'string' &&
                business.addressJson.googleMapLink
                  ? business.addressJson.googleMapLink
                  : undefined
              }
            />
            {website ? (
              <div className="profile-inset space-y-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Globe className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1">
                      Website
                    </p>
                    <p className="text-sm font-medium text-foreground break-words leading-snug">
                      {website.replace(/^https?:\/\/(www\.)?/, '')}
                    </p>
                  </div>
                </div>
                <Button
                  asChild
                  size="sm"
                  className="w-full rounded-xl h-8 px-3 text-xs font-semibold shadow-sm shadow-primary/15"
                >
                  <a
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5"
                  >
                    Open website
                    <Globe className="h-3.5 w-3.5" aria-hidden />
                  </a>
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

type LinkedPerson = NonNullable<PublicBusiness['users']>[number];

function LinkedPersonRow({ user }: { user: LinkedPerson }) {
  const initials = (user.name || '?')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <ProfileLink
      userId={user.id}
      image={user.image}
      name={user.name}
      showAvatar={false}
      showName={false}
      className="block w-full group"
    >
      <div className="profile-inset-interactive flex items-center gap-3 md:gap-4 w-full min-w-0 text-left">
        <Avatar className="h-10 w-10 md:h-11 md:w-11 shrink-0 border-2 border-background shadow-sm ring-2 ring-primary/10 group-hover:ring-primary/25 transition-all">
          <AvatarImage src={user.image} alt="" className="object-cover" />
          <AvatarFallback className="rounded-full bg-gradient-to-br from-primary/15 to-primary/5 text-primary text-xs font-bold">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-sm md:text-base font-semibold font-display text-foreground truncate group-hover:text-primary transition-colors leading-snug">
            {user.name}
          </p>
          {user.role ? (
            <p className="text-xs text-muted-foreground font-medium truncate">{user.role}</p>
          ) : null}
        </div>
      </div>
    </ProfileLink>
  );
}

function ContactItem({
  label,
  value,
  icon,
  href,
}: {
  label: string;
  value?: string | null;
  icon: React.ReactNode;
  href?: string;
}) {
  if (!value) return null;
  const content = (
    <div className="profile-inset-interactive">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1">
            {label}
          </p>
          <p className="text-sm font-medium text-foreground break-words leading-snug">{value}</p>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        className="block"
      >
        {content}
      </a>
    );
  }
  return content;
}
